/**
 * Opens a new print window with a formatted LPG delivery challan.
 * Uses browser's native print → Save as PDF.
 */
export function printChallan(sale, companyName) {
  const fmtINR = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n || 0);

  const items = sale.items || [];
  const itemRows = items.map((item, i) => {
    const kgPerCyl = parseFloat(item.size) || 0;
    const totalKg  = (item.filled || 0) * kgPerCyl;
    const amount   = totalKg * (item.rate_per_kg || 0);
    return `
      <tr>
        <td>${i + 1}</td>
        <td>${item.brand || '—'}</td>
        <td>${item.size || '—'}</td>
        <td class="num">${item.filled || 0}</td>
        <td class="num">${item.mt || 0}</td>
        <td class="num">${kgPerCyl.toFixed(2)}</td>
        <td class="num">${totalKg.toFixed(2)}</td>
        <td class="num">${item.rate_per_kg > 0 ? `₹${item.rate_per_kg.toFixed(2)}` : '—'}</td>
        <td class="num">${item.rate_per_kg > 0 ? fmtINR(amount) : '—'}</td>
      </tr>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Challan ${sale.challan_no || ''} — ${companyName}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 20px; max-width: 800px; margin: auto; }
    .header { text-align: center; border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 14px; }
    .company { font-size: 18px; font-weight: 900; letter-spacing: -0.5px; }
    .doc-title { font-size: 13px; font-weight: bold; letter-spacing: 2px; margin-top: 4px; color: #333; }
    .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; margin-bottom: 14px; }
    .meta-row { display: flex; gap: 8px; }
    .meta-label { font-weight: bold; color: #555; min-width: 90px; }
    .section-title { font-weight: bold; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #666; margin-bottom: 6px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    th { background: #f0f0f0; border: 1px solid #bbb; padding: 5px 7px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
    td { border: 1px solid #ddd; padding: 5px 7px; }
    .num { text-align: right; }
    tr:last-child td { font-weight: bold; background: #fafafa; }
    .totals { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
    .total-box { border: 1px solid #ccc; padding: 8px 12px; border-radius: 4px; }
    .total-label { font-size: 11px; color: #666; }
    .total-value { font-size: 16px; font-weight: 900; }
    .sigs { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-top: 30px; }
    .sig-box { border-top: 1px solid #333; padding-top: 6px; font-size: 11px; color: #555; }
    .footer { margin-top: 20px; font-size: 10px; color: #999; text-align: center; border-top: 1px solid #eee; padding-top: 8px; }
    @media print {
      body { padding: 10px; }
      .no-print { display: none; }
      @page { margin: 10mm; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="company">${companyName}</div>
    <div class="doc-title">DELIVERY CHALLAN</div>
  </div>

  <div class="meta-grid">
    <div>
      <div class="meta-row"><span class="meta-label">Challan No:</span><strong>${sale.challan_no || '—'}</strong></div>
      <div class="meta-row"><span class="meta-label">Date:</span>${sale.date || '—'}</div>
      <div class="meta-row"><span class="meta-label">Godown:</span>${sale.godown || '—'}</div>
    </div>
    <div>
      <div class="meta-row"><span class="meta-label">Customer:</span><strong>${sale.customer_name || 'Walk-in'}</strong></div>
      ${sale.customer_phone ? `<div class="meta-row"><span class="meta-label">Phone:</span>${sale.customer_phone}</div>` : ''}
      <div class="meta-row"><span class="meta-label">Vehicle:</span>${sale.vehicle || '—'}</div>
      ${sale.driver ? `<div class="meta-row"><span class="meta-label">Driver:</span>${sale.driver}</div>` : ''}
    </div>
  </div>

  <div class="section-title">Items</div>
  <table>
    <thead>
      <tr>
        <th>#</th><th>Brand</th><th>Size</th>
        <th class="num">Filled</th><th class="num">MT</th>
        <th class="num">KG/Cyl</th><th class="num">Total KG</th>
        <th class="num">Rate/KG</th><th class="num">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
      <tr>
        <td colspan="3"><strong>TOTAL</strong></td>
        <td class="num">${items.reduce((s,i) => s+(i.filled||0), 0)}</td>
        <td class="num">${items.reduce((s,i) => s+(i.mt||0), 0)}</td>
        <td class="num">—</td>
        <td class="num">${(sale.total_kg || 0).toFixed(2)}</td>
        <td class="num">—</td>
        <td class="num"><strong>${fmtINR(sale.total_amount)}</strong></td>
      </tr>
    </tbody>
  </table>

  <div class="totals">
    <div class="total-box">
      <div class="total-label">Total KG</div>
      <div class="total-value">${(sale.total_kg || 0).toFixed(2)} kg</div>
    </div>
    <div class="total-box">
      <div class="total-label">Total Amount</div>
      <div class="total-value">${fmtINR(sale.total_amount)}</div>
    </div>
  </div>

  ${sale.notes ? `<p style="font-size:11px;color:#555;margin-bottom:16px;"><strong>Notes:</strong> ${sale.notes}</p>` : ''}

  <div class="sigs">
    <div class="sig-box">Driver's Signature</div>
    <div class="sig-box">Customer's Signature</div>
    <div class="sig-box">Authorised Signatory</div>
  </div>

  <div class="footer">This is a computer-generated delivery challan. Subject to jurisdiction.</div>

  <div class="no-print" style="text-align:center;margin-top:20px;">
    <button onclick="window.print()" style="padding:8px 24px;font-size:13px;font-weight:bold;background:#111;color:#fff;border:none;border-radius:6px;cursor:pointer;">
      Print / Save as PDF
    </button>
    <button onclick="window.close()" style="padding:8px 18px;font-size:13px;margin-left:10px;background:#f3f4f6;border:1px solid #ccc;border-radius:6px;cursor:pointer;">
      Close
    </button>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=860,height=700');
  win.document.write(html);
  win.document.close();
}
